import React from 'react';

export interface HJToggleProps {
  on: boolean;
  onChange?: (next: boolean) => void;
  /** Visible label rendered to the right of the toggle. Click also toggles. */
  label?: React.ReactNode;
  disabled?: boolean;
  /** ARIA label when no visible label is provided. */
  ariaLabel?: string;
}

/** Design-system toggle pill. 34×20 with a 16×16 knob — visually distinct
 *  from shadcn's Switch. Brand blue when on, surface-3 when off. */
export const HJToggle: React.FC<HJToggleProps> = ({
  on,
  onChange,
  label,
  disabled,
  ariaLabel,
}) => {
  const handle = () => {
    if (disabled) return;
    onChange?.(!on);
  };
  const knob = (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
      disabled={disabled}
      onClick={handle}
      className="relative flex-shrink-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
      style={{
        width: 34,
        height: 20,
        borderRadius: 999,
        background: on ? 'var(--hj-blue)' : 'var(--hj-surface-3)',
        border: 0,
        padding: 0,
        transition: 'background 120ms ease',
      }}
    >
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          top: 2,
          left: on ? 16 : 2,
          width: 16,
          height: 16,
          background: on ? 'oklch(0.16 0.005 70)' : 'var(--hj-ink-2)',
          transition: 'left 120ms ease, background 120ms ease',
        }}
      />
    </button>
  );

  if (!label) return knob;

  return (
    <label
      className="inline-flex items-center gap-2.5 cursor-pointer min-h-[44px] sm:min-h-0"
      style={{ color: 'var(--hj-ink-2)', fontSize: 13 }}
      onClick={(e) => {
        // Only toggle when the label text itself is clicked — the button
        // handles its own click and we don't want a double-fire.
        if (e.target === e.currentTarget || (e.target as HTMLElement).tagName !== 'BUTTON') {
          // no-op, the button handles it
        }
      }}
    >
      {knob}
      <span onClick={handle}>{label}</span>
    </label>
  );
};
